import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, MapPin, Calendar, Clock, Phone, User, DollarSign, CheckCircle2, XCircle, StickyNote } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

import SEO from "@/components/shared/SEO";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { cleanerApi, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import WorkPhotos from "@/components/cleaner/WorkPhotos";

const TIME_SLOT_LABELS = {
  en: { morning: "Morning (7 AM – 11 AM)", midday: "Midday (11 AM – 2 PM)", afternoon: "Afternoon (2 PM – 5 PM)", evening: "Evening (5 PM – 8 PM)", night: "Night (8 PM – 10 PM)", flexible: "Flexible — anytime" },
  es: { morning: "Mañana (7 AM – 11 AM)", midday: "Mediodía (11 AM – 2 PM)", afternoon: "Tarde (2 PM – 5 PM)", evening: "Tarde-Noche (5 PM – 8 PM)", night: "Noche (8 PM – 10 PM)", flexible: "Flexible — cuando puedas" },
};

export default function CleanerBookingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { locale } = useI18n();
  const L = (en, es) => (locale === "es" ? es : en);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await cleanerApi.get(`/cleaner/bookings/${id}`);
      setBooking(unwrap(r));
    } catch {
      toast.error(L("Could not load this job", "No pudimos cargar este trabajo"));
      navigate("/cleaner/dashboard");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const acceptJob = async () => {
    setAccepting(true);
    try {
      await cleanerApi.post(`/cleaner/bookings/${id}/accept`);
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
      toast.success(L(`Job accepted — see you on ${booking?.service?.preferred_date || "the day"}`,
                      `Trabajo aceptado — te vemos el ${booking?.service?.preferred_date || "día acordado"}`));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not accept the job");
    } finally { setAccepting(false); }
  };

  const declineJob = async () => {
    setDeclining(true);
    try {
      await cleanerApi.post(`/cleaner/bookings/${id}/decline`);
      toast.success(L("Job declined", "Trabajo rechazado"));
      navigate("/cleaner/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not decline the job");
    } finally { setDeclining(false); setDeclineOpen(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#FAFAF9]"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" /></div>;
  if (!booking) return null;

  const cust = booking.customer || {};
  const svc = booking.service || {};
  const accepted = booking.assignment_status === "accepted";
  const timeLabel = (TIME_SLOT_LABELS[locale] || TIME_SLOT_LABELS.en)[svc.preferred_time_slot] || svc.preferred_time_slot;

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="cleaner-booking-detail-page">
      <SEO title={L("Job details — Ever Clean Home", "Detalle del trabajo — Ever Clean Home")} noindex />
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 flex items-center">
          <button onClick={() => navigate("/cleaner/dashboard")} className="text-sm text-[#4B5563] hover:text-[#0EA5A4] flex items-center gap-1" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> {L("Back to dashboard", "Volver al panel")}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-10 space-y-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tighter">#{booking.booking_number}</h1>
            <p className="text-[#4B5563] text-sm mt-1">{cust.full_name}</p>
          </div>
          {accepted && (
            <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50" data-testid="accepted-badge">
              <CheckCircle2 className="w-3 h-3 mr-1" /> {L("Accepted", "Aceptado")}
            </Badge>
          )}
        </div>

        <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><User className="w-4 h-4" /> {L("Customer", "Cliente")}</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-[#4B5563]">{L("Name", "Nombre")}</dt><dd className="font-semibold">{cust.full_name}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563] flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {L("Phone", "Teléfono")}</dt><dd className="font-semibold">{cust.phone || "—"}</dd></div>
            {cust.email && <div className="flex justify-between"><dt className="text-[#4B5563]">{L("Email", "Correo")}</dt><dd className="font-semibold">{cust.email}</dd></div>}
            <div className="flex justify-between gap-4"><dt className="text-[#4B5563] flex items-center gap-1 shrink-0"><MapPin className="w-3.5 h-3.5" /> {L("Address", "Dirección")}</dt>
              <dd className="font-semibold text-right">{cust.address_line1}{cust.address_line2 ? `, ${cust.address_line2}` : ""}<br />{cust.city}, {cust.state} {cust.zip_code}</dd></div>
          </dl>
        </Card>

        <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> {L("Service", "Servicio")}</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between"><dt className="text-[#4B5563]">{L("Date", "Fecha")}</dt><dd className="font-semibold">{svc.preferred_date || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563] flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {L("Time", "Horario")}</dt><dd className="font-semibold">{timeLabel}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">{L("Property", "Propiedad")}</dt><dd className="font-semibold capitalize">{svc.property_type}</dd></div>
            <div className="flex justify-between"><dt className="text-[#4B5563]">{L("Size", "Tamaño")}</dt><dd className="font-mono font-semibold">{svc.square_feet} sq ft</dd></div>
            {svc.property_type === "other" && svc.space_description && (
              <div className="flex justify-between gap-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="space-description-row">
                <dt className="text-amber-800 font-semibold">{L("Space type", "Tipo de espacio")}</dt>
                <dd className="font-semibold text-right text-amber-900">{svc.space_description}</dd>
              </div>
            )}
            <div className="flex justify-between"><dt className="text-[#4B5563]">{L("Bedrooms / Bathrooms", "Dormitorios / Baños")}</dt><dd className="font-mono font-semibold">{svc.bedrooms} BR · {svc.bathrooms} BA · {svc.half_bathrooms} ½</dd></div>
            <div><dt className="text-[#4B5563] mb-1">{L("Tasks", "Tareas")}</dt>
              <dd className="flex flex-wrap gap-1.5 mt-1">
                {(svc.tasks || []).map((t) => <Badge key={t} variant="outline" className="text-xs capitalize">{t.replace(/_/g, " ")}</Badge>)}
              </dd>
            </div>
            {svc.additional_notes && (
              <div className="pt-1">
                <dt className="text-[#4B5563] mb-1 flex items-center gap-1"><StickyNote className="w-3.5 h-3.5" /> {L("Customer notes", "Notas del cliente")}</dt>
                <dd className="text-sm whitespace-pre-wrap p-3 rounded-xl bg-[#F5F5F4]" data-testid="customer-notes">{svc.additional_notes}</dd>
              </div>
            )}
          </dl>
        </Card>

        <Card className="rounded-2xl p-6 border border-emerald-200 bg-emerald-50/50">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2 text-emerald-800"><DollarSign className="w-4 h-4" /> {L("Payment summary", "Resumen de pago")}</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between">
              <dt className="font-bold text-emerald-800">{L("You'll earn", "Vas a ganar")}</dt>
              <dd className="font-mono text-lg font-bold text-emerald-700" data-testid="earning-amount">${booking.payment_summary?.cleaner_earnings?.toFixed(2)}</dd>
            </div>
          </dl>
        </Card>

        {accepted && (
          <WorkPhotos
            bookingId={booking.id || id}
            bookingStatus={booking.status}
            initialPhotos={booking.work_photos || []}
            L={L}
          />
        )}

        {!accepted && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setDeclineOpen(true)} disabled={declining} variant="outline"
              className="rounded-xl text-rose-600 border-rose-300 hover:bg-rose-50 hover:text-rose-700 flex-1" data-testid="decline-detail-btn">
              <XCircle className="w-4 h-4 mr-2" /> {L("Decline", "Rechazar")}
            </Button>
            <Button onClick={acceptJob} disabled={accepting} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex-1" data-testid="accept-detail-btn">
              {accepting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} {L("Accept job", "Aceptar trabajo")}
            </Button>
          </div>
        )}
      </main>

      <AlertDialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <AlertDialogContent data-testid="decline-job-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{L("Decline this job?", "¿Rechazar este trabajo?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {L("Are you sure you want to decline this job? You won't be able to reverse this.",
                 "¿Estás seguro de que quieres rechazar este trabajo? No vas a poder revertirlo.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="decline-job-cancel-btn">{L("Cancel", "Cancelar")}</AlertDialogCancel>
            <AlertDialogAction onClick={declineJob} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="decline-job-confirm-btn">
              {L("Decline job", "Rechazar trabajo")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
